import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useContext, useState, useEffect } from "react";
import NavigationButtons from "../../components/NavigationButtons";
import Selection from "../../components/Selection";
import { Context } from "../../context/FittingContext";
import WebSocketConnection from "../../utils/WebSocketConnection";
import {
	BALL_FITTING_BASE_URL,
	BALL_FITTING_SPECS,
	applySpec,
	CLUB_TYPES,
	URL_PARAMS,
	CLUB_CONVERSION_FORMULAS
} from "../../constants/ballFitting";
import { fetchGolfBalls } from "../../api/ScoringApi";
import { convertMsToMph } from "../../utils/conversions";

const BallSelection = ({ increaseStep, decreaseStep, currentStep }) => {
	const [ballSuggestions, setBallSuggestions] = useState({});
	const [displayBallSuggestions, setDisplayBallSuggestions] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [ballFittingCompleted, setBallFittingCompleted] = useState(false);
	const [showFittingInfo, setShowFittingInfo] = useState(true);
	const { clubState, setSelectedBall, setBallOptions } = useContext(Context);

	useEffect(() => {
		const completed = localStorage.getItem("ballFittingCompleted") === "true";
		setBallFittingCompleted(completed);
		if (completed) {
			getBallSuggestions();
			setShowFittingInfo(false); // Collapse the fitting info when already completed
		}
	}, []);

	const calculateAverages = () => {
		const sessionData = JSON.parse(localStorage.getItem("sessionData"));
		if (!sessionData || !sessionData.swingData || sessionData.swingData.length === 0) {
			return null;
		}

		let totalSwings = 0;
		let totals = {
			BallSpeed: 0,
			LaunchAngle: 0,
			SpinRate: 0
		};

		sessionData.swingData.forEach((swingSession) => {
			swingSession.data.forEach((swing) => {
				if (swing.BallSpeed && swing.LaunchAngle && swing.SpinRate) {
					totals.BallSpeed += swing.BallSpeed;
					totals.LaunchAngle += swing.LaunchAngle;
					totals.SpinRate += swing.SpinRate;
					totalSwings++;
				}
			});
		});

		if (totalSwings === 0) return null;

		const clubType = localStorage.getItem("clubType") || CLUB_TYPES.DRIVERS;
		const specs = BALL_FITTING_SPECS[clubType];

		const avgBallSpeed = totals.BallSpeed / totalSwings;
		const avgLaunchAngle = totals.LaunchAngle / totalSwings;
		const avgSpinRate = totals.SpinRate / totalSwings;

		return {
			ballSpeed: applySpec(convertMsToMph(avgBallSpeed), specs.ballSpeed),
			launch: applySpec(avgLaunchAngle, specs.launchAngle),
			spin: applySpec(avgSpinRate, specs.spinRate)
		};
	};

	const calculateBothClubTypes = (averages, clubType) => {
		let driverValues, ironValues;

		if (clubType === CLUB_TYPES.DRIVERS) {
			// We have driver data, calculate iron values
			driverValues = {
				ballSpeed: averages.ballSpeed,
				launch: averages.launch,
				spin: averages.spin
			};

			// Convert driver values to iron values using formulas
			const rawIronValues = {
				ballSpeed: CLUB_CONVERSION_FORMULAS.driverToIron.ballSpeed(averages.ballSpeed),
				launch: CLUB_CONVERSION_FORMULAS.driverToIron.launchAngle(averages.launch),
				spin: CLUB_CONVERSION_FORMULAS.driverToIron.spinRate(averages.spin)
			};

			// Apply iron specifications to the calculated values
			const ironSpecs = BALL_FITTING_SPECS.Irons;
			ironValues = {
				ballSpeed: applySpec(rawIronValues.ballSpeed, ironSpecs.ballSpeed),
				launch: applySpec(rawIronValues.launch, ironSpecs.launchAngle),
				spin: applySpec(rawIronValues.spin, ironSpecs.spinRate)
			};
		} else {
			// We have iron data, calculate driver values
			ironValues = {
				ballSpeed: averages.ballSpeed,
				launch: averages.launch,
				spin: averages.spin
			};

			// Convert iron values to driver values using formulas
			const rawDriverValues = {
				ballSpeed: CLUB_CONVERSION_FORMULAS.ironToDriver.ballSpeed(averages.ballSpeed),
				launch: CLUB_CONVERSION_FORMULAS.ironToDriver.launchAngle(averages.launch),
				spin: CLUB_CONVERSION_FORMULAS.ironToDriver.spinRate(averages.spin)
			};

			// Apply driver specifications to the calculated values
			const driverSpecs = BALL_FITTING_SPECS.Drivers;
			driverValues = {
				ballSpeed: applySpec(rawDriverValues.ballSpeed, driverSpecs.ballSpeed),
				launch: applySpec(rawDriverValues.launch, driverSpecs.launchAngle),
				spin: applySpec(rawDriverValues.spin, driverSpecs.spinRate)
			};
		}

		return { driverValues, ironValues };
	};

	const handleGetBallSelections = () => {
		const averages = calculateAverages();
		const clubType = localStorage.getItem("clubType") || CLUB_TYPES.DRIVERS;

		// Calculate values for both club types
		const { driverValues, ironValues } = calculateBothClubTypes(averages, clubType);

		// Build URL with all 6 parameters
		const driverParams = URL_PARAMS.DRIVER;
		const ironParams = URL_PARAMS.IRON;

		const urlParams = [
			`${driverParams.ballSpeed}=${driverValues.ballSpeed}`,
			`${driverParams.launch}=${driverValues.launch}`,
			`${driverParams.spin}=${driverValues.spin}`,
			`${ironParams.ballSpeed}=${ironValues.ballSpeed}`,
			`${ironParams.launch}=${ironValues.launch}`,
			`${ironParams.spin}=${ironValues.spin}`
		].join('&');

		const url = `${BALL_FITTING_BASE_URL}?${urlParams}`;
		window.open(url, '_blank');

		// Show confirmation dialog when user returns (after a short delay)
		setTimeout(() => {
			if (!ballFittingCompleted) {
				setShowConfirmationDialog(true);
			}
		}, 1000);
	};

	const handleConfirmBallFitting = (completed) => {
		setShowConfirmationDialog(false);
		if (completed) {
			setBallFittingCompleted(true);
			localStorage.setItem("ballFittingCompleted", "true");
			setShowFittingInfo(false); // Collapse the fitting info after confirmation
			getBallSuggestions();
		}
	};

	const getBallSuggestions = async () => {
		fetchGolfBalls((result) => {
			if (result.success) {
				const ballsWithId = result.data.map((ball, index) => ({
					...ball,
					id: index
				}));

				const suggestions = {
					data: ballsWithId,
					uniqueFamily: [...new Set(ballsWithId.map(ball => ball.family))],
					uniqueMan: [...new Set(ballsWithId.map(ball => ball.manufacturer))]
				};

				setBallSuggestions(suggestions);
				setBallOptions(suggestions.data);
				setDisplayBallSuggestions(true);
			} else {
				console.error("Failed to fetch golf balls data");
			}
		});
	};

	return (
		<>
			<NavigationButtons
				onBackClick={() => {
					decreaseStep();
					WebSocketConnection.setOnMessageCallback(undefined);
					WebSocketConnection.captureData = false;
				}}
				onNextClick={() => {
					increaseStep();
					WebSocketConnection.setOnMessageCallback(undefined);
					WebSocketConnection.captureData = false;
				}}
				stepPosition={currentStep}
			/>
			<Grid container sx={{ paddingX: 5, paddingTop: 1, paddingBottom: 5 }}>
				<Grid item xs={12}>
					<Typography variant="h4">
						Select a ball or continue to end the session
					</Typography>
					<Typography variant="subtitle1">
						Get personalized ball fitting recommendations based on the user's swing data.
					</Typography>
				</Grid>

				<Grid item xs={12} sx={{ marginTop: 3 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
						<Typography variant="h5" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
							Ball Fitting
							{ballFittingCompleted && (
								<CheckCircleIcon color="success" fontSize="medium" />
							)}
						</Typography>
						<IconButton
							onClick={() => setShowFittingInfo(!showFittingInfo)}
							aria-label={showFittingInfo ? "collapse" : "expand"}
						>
							{showFittingInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</Box>

					{/* Collapsible Ball Fitting Info Section */}
					<Collapse in={showFittingInfo}>
						{(() => {
							const averages = calculateAverages();
							if (!averages) return null;

							const clubType = localStorage.getItem("clubType") || CLUB_TYPES.DRIVERS;
							const { driverValues, ironValues } = calculateBothClubTypes(averages, clubType);
							const isMeasuredDriver = clubType === CLUB_TYPES.DRIVERS;

							return (
								<>
									<Card sx={{ marginBottom: 3, backgroundColor: '#f5f5f5' }}>
										<CardContent>

											{/* Driver Values */}
											<Box sx={{ marginBottom: 2 }}>
												<Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
													Driver {isMeasuredDriver ? '(Measured)' : '(Estimated)'}
												</Typography>
												<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 2 }}>
													<Typography variant="body1">
														<strong>Ball Speed:</strong> {driverValues.ballSpeed.toFixed(1)} mph
													</Typography>
													<Typography variant="body1">
														<strong>Launch Angle:</strong> {driverValues.launch.toFixed(1)}°
													</Typography>
													<Typography variant="body1">
														<strong>Spin Rate:</strong> {Math.round(driverValues.spin)} rpm
													</Typography>
												</Box>
											</Box>

											{/* Iron Values */}
											<Box sx={{ marginBottom: 1 }}>
												<Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
													Iron {!isMeasuredDriver ? '(Measured)' : '(Estimated)'}
												</Typography>
												<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 2 }}>
													<Typography variant="body1">
														<strong>Ball Speed:</strong> {ironValues.ballSpeed.toFixed(1)} mph
													</Typography>
													<Typography variant="body1">
														<strong>Launch Angle:</strong> {ironValues.launch.toFixed(1)}°
													</Typography>
													<Typography variant="body1">
														<strong>Spin Rate:</strong> {Math.round(ironValues.spin)} rpm
													</Typography>
												</Box>
											</Box>

											<Typography variant="body2" sx={{ marginTop: 2, fontStyle: 'italic' }}>
												These are the values that will be used for your ball fitting recommendations.
											</Typography>
										</CardContent>
									</Card>

									<Box display="flex" gap={2} sx={{ marginBottom: 3 }}>
										<Button
											variant="contained"
											onClick={handleGetBallSelections}
											color="primary"
										>
											Get Ball Suggestions
										</Button>
									</Box>
								</>
							);
						})()}
					</Collapse>
				</Grid>

				{displayBallSuggestions && ballSuggestions.data && (
					<Grid item xs={12}>
						<Selection
							headerText="Ball Selection"
							currentSelection={clubState.selectedBall || {}}
							onSelection={(selection) => setSelectedBall(selection)}
							tableRows={ballSuggestions.data || []}
							famFilterOpts={ballSuggestions.uniqueFamily || []}
							manFilterOpts={ballSuggestions.uniqueMan || []}
							dataType="ball"
						/>
					</Grid>
				)}
			</Grid>

			{/* Ball Fitting Confirmation Dialog */}
			<Dialog
				open={showConfirmationDialog}
				onClose={() => setShowConfirmationDialog(false)}
				aria-labelledby="ball-fitting-confirmation-title"
				aria-describedby="ball-fitting-confirmation-description"
			>
				<DialogTitle id="ball-fitting-confirmation-title">
					Ball Fitting Completed?
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="ball-fitting-confirmation-description">
						Are you ready to select a ball based on the fitting recommendations?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleConfirmBallFitting(false)} color="secondary">
						No
					</Button>
					<Button onClick={() => handleConfirmBallFitting(true)} color="primary" variant="contained">
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BallSelection;
